import { MaterialSymbol } from '@florencelabs/icons';
import { Tooltip } from '@florencelabs/tooltip';
import { addDays, format, startOfDay } from 'date-fns';
import React, { FC } from 'react';
import { Button } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { LoadingGradient } from '@provider-directory/components/loading-gradient/loading-gradient.component';

export type DateNavigationDirection = 'forward' | 'back';

interface DateNavigationControlsProps {
  isLoading?: boolean;
  startDate: Date;
  dateRange?: number;
  isNextDisabled: boolean;
  maxDays: number;
  onNavigation: (direction: DateNavigationDirection) => void;
}

export const DateNavigationControls: FC<DateNavigationControlsProps> = ({
  isLoading,
  startDate,
  dateRange = 3,
  isNextDisabled,
  maxDays,
  onNavigation,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className={'flex justify-between'}>
        <LoadingGradient className={'h-[26px] w-[100px] rounded-sm'} />
        <LoadingGradient className={'h-[26px] w-[50px] rounded-sm'} />
      </div>
    );
  }

  return (
    <div className={'flex justify-between'}>
      <span className={'text-body-small-medium'}>
        {format(startDate, 'MMM d')} -{' '}
        {format(addDays(startDate, dateRange), 'MMM d')}
      </span>
      <div className={'gap-1'}>
        <Button
          aria-label={t('appointmentSlot.appointmentDate.previousDay')}
          className={'disabled:text-gray-950/30'}
          onPress={() => {
            onNavigation('back');
          }}
          isDisabled={startOfDay(startDate) <= new Date()}
        >
          <MaterialSymbol name={'chevron_left'} size={24} />
        </Button>
        {isNextDisabled ? (
          <Tooltip
            tooltipText={t('appointmentSlot.appointmentDate.maxDateTooltip', {
              maxDays,
            })}
          >
            <Button
              aria-label={t('appointmentSlot.appointmentDate.nextDay')}
              className={'cursor-default text-gray-950/30'}
            >
              <MaterialSymbol name={'chevron_right'} size={24} />
            </Button>
          </Tooltip>
        ) : (
          <Button
            aria-label={t('appointmentSlot.appointmentDate.nextDay')}
            onPress={() => {
              onNavigation('forward');
            }}
          >
            <MaterialSymbol name={'chevron_right'} size={24} />
          </Button>
        )}
      </div>
    </div>
  );
};
